/**
 * Admin Guard Component
 *
 * Protects admin routes by checking for admin roles.
 * Returns 404 for non-admins to hide the existence of admin console.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Loader2 } from 'lucide-react';

interface AdminGuardProps {
  children: ReactNode;
  requiredRole?: 'superadmin' | 'admin' | 'support' | 'dev';
}

// Simple 404 component that doesn't reveal admin exists
function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(220,27%,8%)]">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-white">404</h1>
        <p className="mb-4 text-xl text-gray-400">Page not found</p>
        <a
          href="/"
          className="text-[hsl(220,70%,60%)] underline hover:text-[hsl(220,70%,70%)]"
        >
          Return to Home
        </a>
      </div>
    </div>
  );
}

// Loading state
function LoadingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(220,27%,8%)]">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    </div>
  );
}

export function AdminGuard({ children, requiredRole }: AdminGuardProps) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isAdminUser, adminRole, isLoading: adminLoading, isSuperadmin } = useAdminAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (authLoading || adminLoading) {
    return <LoadingPage />;
  }

  // Not authenticated - redirect to auth page with return URL
  // (This is normal behavior, not revealing admin exists)
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // Authenticated but not an admin - show 404
  // This hides the existence of the admin console
  if (!isAdminUser) {
    return <NotFoundPage />;
  }

  // Check specific role if required
  if (requiredRole) {
    // Superadmin has access to everything
    if (isSuperadmin) {
      return <>{children}</>;
    }

    // Check role hierarchy
    const roleHierarchy: Record<string, number> = {
      superadmin: 4,
      admin: 3,
      support: 2,
      dev: 1,
    };

    const userLevel = adminRole ? roleHierarchy[adminRole] || 0 : 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    // For admin role, need admin or superadmin
    // For support role, need support, admin, or superadmin
    // For dev role, only dev or superadmin (dev is special track)
    if (requiredRole === 'dev') {
      if (adminRole !== 'dev' && adminRole !== 'superadmin') {
        return <NotFoundPage />;
      }
    } else if (userLevel < requiredLevel) {
      return <NotFoundPage />;
    }
  }

  // All checks passed
  return <>{children}</>;
}

/**
 * HOC version for wrapping components
 */
export function withAdminGuard<P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: 'superadmin' | 'admin' | 'support' | 'dev'
) {
  return function AdminGuardedComponent(props: P) {
    return (
      <AdminGuard requiredRole={requiredRole}>
        <Component {...props} />
      </AdminGuard>
    );
  };
}
